import { useCallback, useEffect, useRef, useState } from 'react';

type ScrollDirection = 'up' | 'down' | null;

export type UseScrollDirectionOptions = {
  threshold?: number;
  elementOriginalY?: number;
};

export function useScrollDirection({
  threshold = 30,
  elementOriginalY,
}: UseScrollDirectionOptions = {}) {
  const [direction, setDirection] = useState<ScrollDirection>(null);
  const [isPastOriginalPosition, setIsPastOriginalPosition] = useState(false);

  const scrollYRef = useRef(0);
  const lastDirectionScrollY = useRef(0);
  const directionRef = useRef<ScrollDirection>(null);
  const isPastOriginalPositionRef = useRef(false);
  const elementOriginalYRef = useRef(elementOriginalY);
  const isFrameScheduledRef = useRef(false);
  const frameRef = useRef<number | null>(null);

  const updateIsPastOriginalPosition = useCallback(
    (
      currentScrollY: number,
      options?: { syncDirectionOnCrossing?: boolean },
    ) => {
      const pastY = elementOriginalYRef.current;

      if (pastY === undefined) {
        return;
      }

      const nextIsPast = currentScrollY > pastY;

      if (nextIsPast === isPastOriginalPositionRef.current) {
        return;
      }

      isPastOriginalPositionRef.current = nextIsPast;
      setIsPastOriginalPosition(nextIsPast);
      lastDirectionScrollY.current = currentScrollY;

      if (options?.syncDirectionOnCrossing) {
        const crossingDirection: ScrollDirection = nextIsPast ? 'down' : 'up';

        directionRef.current = crossingDirection;
        setDirection(crossingDirection);
      }
    },
    [],
  );

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;

    scrollYRef.current = currentScrollY;
    updateIsPastOriginalPosition(currentScrollY, {
      syncDirectionOnCrossing: true,
    });

    const diff = currentScrollY - lastDirectionScrollY.current;

    if (Math.abs(diff) < threshold) {
      return;
    }

    const nextDirection: ScrollDirection = diff > 0 ? 'down' : 'up';

    if (nextDirection !== directionRef.current) {
      directionRef.current = nextDirection;
      setDirection(nextDirection);
    }

    lastDirectionScrollY.current = currentScrollY;
  }, [threshold, updateIsPastOriginalPosition]);

  const scheduleHandleScroll = useCallback(() => {
    if (isFrameScheduledRef.current) {
      return;
    }

    isFrameScheduledRef.current = true;
    frameRef.current = window.requestAnimationFrame(() => {
      isFrameScheduledRef.current = false;
      frameRef.current = null;
      handleScroll();
    });
  }, [handleScroll]);

  useEffect(() => {
    elementOriginalYRef.current = elementOriginalY;
    updateIsPastOriginalPosition(scrollYRef.current);
  }, [elementOriginalY, updateIsPastOriginalPosition]);

  useEffect(() => {
    const initialScrollY = window.scrollY;

    lastDirectionScrollY.current = initialScrollY;
    scrollYRef.current = initialScrollY;
    updateIsPastOriginalPosition(initialScrollY);

    window.addEventListener('scroll', scheduleHandleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', scheduleHandleScroll);

      isFrameScheduledRef.current = false;

      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [scheduleHandleScroll, updateIsPastOriginalPosition]);

  return { direction, isPastOriginalPosition };
}
